/**
 * Micro-copy for the waits people actually watch — scanning, listening,
 * fetching a recipe page, signing in. Short by requirement (≤ 40 chars at
 * maximal context: there is no figure to interpolate, so "maximal" is just
 * the line itself); light rather than roast-heavy, the way `what_fits.base`
 * is playful rather than cutting — nobody needs to be mocked while waiting.
 * Bucketed by `ctx.surface`.
 *
 * First person, not the impersonal passive: „Skaitoma…" is a machine
 * reporting on itself, „Skaitau…" is someone in the room doing something for
 * you. Same length, entirely different presence.
 */

import type { SassContext } from '@gramas/shared'

type Line = (ctx: SassContext) => string

export const LOADING: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'loading.scan': [
    () => 'Skaitau…',
    () => 'Įsižiūriu…',
    () => 'Aiškinuosi etiketę…',
    () => 'Ieškau raidžių…',
    () => 'Palauk, dar neaišku…',
  ],
  'loading.voice': [
    () => 'Klausausi…',
    () => 'Rūšiuoju žodžius…',
    () => 'Bandau suprasti…',
    () => 'Girdžiu, girdžiu…',
  ],
  'loading.import': [
    () => 'Skaitau puslapį…',
    () => 'Ieškau ingredientų…',
    () => 'Naršau po receptą…',
    () => 'Renku, kas valgoma…',
  ],
  'loading.login': [
    () => 'Jungiuosi…',
    () => 'Beldžiuosi į serverį…',
    () => 'Tikrinu, ar tai tu…',
    () => 'Durys prasiveria…',
  ],
  // Four surfaces that used to spin in silence. A wait with nothing on it is
  // the one place in the app where she is provably not there — and the photo
  // one had a hard-coded „Atpažįstama…" in the component, which is the passive
  // voice this whole file exists to avoid.
  'loading.photo': [
    () => 'Žiūriu į nuotrauką…',
    () => 'Bandau atpažinti…',
    () => 'Skaičiuoju, kas lėkštėje…',
    () => 'Spėlioju iš vaizdo…',
  ],
  'loading.search': [
    () => 'Ieškau…',
    () => 'Verčiu katalogą…',
    () => 'Kažkur čia buvo…',
    () => 'Raustuosi lentynose…',
  ],
  'loading.foods': [
    () => 'Renku sąrašą…',
    () => 'Traukiu iš atminties…',
    () => 'Tuoj bus…',
    () => 'Dar sekundę…',
  ],
  'loading.sync': [
    () => 'Susitariu su serveriu…',
    () => 'Perduodu, ką turiu…',
    () => 'Sinchronizuoju…',
    () => 'Suvedu galus…',
  ],
}

export const LOADING_META: Readonly<Record<string, number>> = {
  'loading.scan': 3,
  'loading.voice': 2,
  'loading.import': 3,
  'loading.login': 3,
  'loading.photo': 3,
  'loading.search': 3,
  'loading.foods': 3,
  'loading.sync': 2,
}
